/**
 * A Modelling month's Financial tab (Chris, 2026-10-28) — the month's BUILT,
 * date-by-date figures, current vs modelled.
 *
 * ONE owner: the month's rows of the season table (yearRows) totalled by
 * YearComparePanel.tsx::yearTotals → monthFigures, exactly as its row in the
 * SEASON table, the workbook Summary and the board paper read them. It used to
 * show the average-week engine check of the design on screen, which could say
 * "NOT AN ANSWER — 392 unreturned" for a month the season build had valued
 * day for day inside the Scope — two owners of one fact (law 8).
 */
import type { ReactNode } from 'react'
import { Callout } from './ui'
import { yearTotals, type YearRow, type YearSideSnap, type YearTotalsSide } from './YearComparePanel'

const num0 = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 0 })
const gbp = (n: number) => `${n < 0 ? '-' : ''}£${num0.format(Math.round(Math.abs(n)))}`

export interface MonthFinancialPanelProps {
  /** This month's rows of the season table (normally one). */
  rows: YearRow[]
  monthLabel: string
  /** Optional scope note (e.g. Pullman dining excluded from "current"). */
  scopeNote?: ReactNode
}

const turnsTxt = (s: YearSideSnap | null) =>
  !s ? '—' : s.servicesRange ? `${s.servicesRange[0]}–${s.servicesRange[1]}` : num0.format(s.services)
const locos = (s: YearSideSnap | null) => (s ? s.steamN + s.dieselN : null)

export function MonthFinancialPanel({ rows, monthLabel, scopeNote }: MonthFinancialPanelProps) {
  const t = yearTotals(rows)
  const cur = t.cur, mod = t.mod
  const days = t.runningDays
  const r0 = rows[0] ?? null
  const issues = rows.flatMap((r) => r.issues ?? [])

  if (rows.length === 0 || (!cur && !mod)) {
    return (
      <Callout kind="warn" title={`${monthLabel} has no figures yet`}>
        Press <strong>★ Build Model</strong> on the stage bar — every month is built and valued day by day, and its figures appear here and in the SEASON table.
      </Callout>
    )
  }

  type Row = { label: string; a: number | null; b: number | null; fmt: (n: number) => string; upGood: boolean }
  const perDay = (side: YearTotalsSide | null, v: number | null | undefined) => (side && v != null && days > 0 ? v / days : null)
  const measures: Row[] = [
    { label: 'Revenue', a: cur?.revenue ?? null, b: mod?.revenue ?? null, fmt: gbp, upGood: true },
    { label: 'Fixed cost', a: cur?.fixed ?? null, b: mod?.fixed ?? null, fmt: gbp, upGood: false },
    { label: 'Variable cost', a: cur?.variable ?? null, b: mod?.variable ?? null, fmt: gbp, upGood: false },
    { label: 'Total cost', a: cur?.costs ?? null, b: mod?.costs ?? null, fmt: gbp, upGood: false },
    { label: 'Contribution', a: cur?.profit ?? null, b: mod?.profit ?? null, fmt: gbp, upGood: true },
    { label: 'Passengers carried', a: cur?.carried ?? null, b: mod?.carried ?? null, fmt: (n) => num0.format(Math.round(n)), upGood: true },
    { label: 'Passengers carried / running day', a: perDay(cur, cur?.carried), b: perDay(mod, mod?.carried), fmt: (n) => num0.format(Math.round(n)), upGood: true },
    { label: 'Turned away by timing', a: cur?.turnedAway ?? null, b: mod?.turnedAway ?? null, fmt: (n) => num0.format(Math.round(n)), upGood: false },
    { label: 'Locos (largest day)', a: locos(r0?.cur ?? null), b: locos(r0?.mod ?? null), fmt: (n) => String(Math.round(n)), upGood: false },
  ]

  const changed: string[] = []
  if (!mod) changed.push(`${monthLabel} is not built yet — press ★ Build Model; the current side is shown on its own.`)
  else {
    if (r0?.cur && r0?.mod && turnsTxt(r0.cur) !== turnsTxt(r0.mod)) changed.push(`Passenger workings per day: ${turnsTxt(r0.cur)} today → ${turnsTxt(r0.mod)} modelled.`)
    const lc = locos(r0?.cur ?? null), lm = locos(r0?.mod ?? null)
    if (lc != null && lm != null && lc !== lm) changed.push(`Locos on the largest day: ${lc} → ${lm}.`)
    if (cur) {
      const d = mod.profit - cur.profit
      changed.push(`Contribution ${d >= 0 ? 'rises' : 'falls'} ${gbp(Math.abs(d))} over the month's ${days} running day${days === 1 ? '' : 's'} (${gbp(cur.profit)} → ${gbp(mod.profit)}).`)
      const dp = mod.carried - cur.carried
      if (Math.abs(dp) >= 0.5) changed.push(`${num0.format(Math.round(Math.abs(dp)))} ${dp > 0 ? 'more' : 'fewer'} passengers carried (${num0.format(Math.round(cur.carried))} → ${num0.format(Math.round(mod.carried))}).`)
      if (cur.turnedAway != null && mod.turnedAway != null && Math.abs(mod.turnedAway - cur.turnedAway) >= 0.5) {
        changed.push(`Turned away by timing: ${num0.format(Math.round(cur.turnedAway))} today → ${num0.format(Math.round(mod.turnedAway))} modelled.`)
      }
    }
    changed.push(issues.length === 0 ? 'Inside the Scope — no intervention needed.' : `The Scope bound: ${issues.map((i) => i.text).join(' · ')} — see ⚠ Interventions.`)
  }

  const hero = (label: string, side: YearTotalsSide | null, modelled: boolean) => (
    <div className={`sh-chero${modelled ? ' sh-chero--modelled' : ''}`}>
      <div className="sh-chero-label">{label}</div>
      <div className="sh-chero-value sh-mono">{side ? gbp(side.profit) : '—'}</div>
      <div className="sh-chero-sub">
        {side
          ? <>contribution · {days} running days · {num0.format(Math.round(side.carried))} carried{side.turnedAway != null ? ` · ${num0.format(Math.round(side.turnedAway))} turned away` : ''}
            {modelled && cur ? <> · {side.profit - cur.profit >= 0 ? '▲' : '▼'} {gbp(Math.abs(side.profit - cur.profit))} vs today</> : null}</>
          : 'not built yet'}
      </div>
    </div>
  )

  return (
    <>
      <div className="sh-compare-heroes">
        {hero(`Current — ${monthLabel}, the real timetable`, cur, false)}
        {hero(`Modelled — ${monthLabel}, as built`, mod, true)}
      </div>

      <section className="sh-card">
        <div className="sh-card-head">
          <span className="sh-card-title">Measures — current vs modelled · {monthLabel}</span>
          <span className="sh-card-note">the month as built, valued date by date · the same figures as its row in the SEASON table</span>
        </div>
        <div className="stu-scroll">
          <table className="stu-table sh-cmp-table">
            <thead><tr><th>Measure</th><th className="num">Current</th><th className="num">Modelled</th><th className="num">Change</th><th>Magnitude</th></tr></thead>
            <tbody>
              {measures.map((r) => {
                if (r.a == null && r.b == null) return null
                const both = r.a != null && r.b != null
                const chg = both ? r.b! - r.a! : 0
                const good = r.upGood ? chg >= 0 : chg <= 0
                const barPct = both ? Math.min(100, Math.round((Math.abs(chg) / Math.max(1, Math.abs(r.a!), Math.abs(r.b!))) * 100)) : 0
                const sign = chg > 0 ? '+' : chg < 0 ? '−' : ''
                return (
                  <tr key={r.label}>
                    <td>{r.label}</td>
                    <td className="num sh-mono">{r.a != null ? r.fmt(r.a) : '—'}</td>
                    <td className="num sh-mono">{r.b != null ? r.fmt(r.b) : '—'}</td>
                    <td className={`num sh-mono${both ? (good ? ' sh-delta--good' : ' sh-delta--bad') : ' sh-dim'}`}>{both ? `${sign}${r.fmt(Math.abs(chg))}` : '—'}</td>
                    <td>{both && <span className="sh-cmp-bar"><span className={`sh-cmp-bar-fill ${good ? 'sh-cmp-bar-fill--good' : 'sh-cmp-bar-fill--bad'}`} style={{ width: `${barPct}%` }} /></span>}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </section>

      <section className="sh-changed">
        <div className="sh-changed-head">What actually changed</div>
        <ul>{changed.map((c, i) => <li key={i}>{c}</li>)}</ul>
        {scopeNote && <p className="stu-note" style={{ margin: '6px 0 0' }}>{scopeNote}</p>}
      </section>
    </>
  )
}
